/**
 * La cotización en HOJA CARTA, con la plantilla de Softfi (2026-09-29).
 *
 * Igual que el recibo en carta (`pos/ReciboEnCarta.tsx`), con lo propio de un
 * presupuesto: hasta cuándo valen los precios, lo que se suma solo si paga en
 * divisas, y que NO es una factura. Los montos son los mismos que el papel del
 * rollo (`papelDeCotizacion.componerCotizacion`).
 */

import { useQuery } from "@tanstack/react-query";
import { HojaCarta, domicilioFiscalDe, ROJO, TENUE, imprimirHojaCarta, type TotalDeHoja } from "@/components/HojaCarta";
import { useSucursales } from "@/components/SucursalActual";
import { contactoDelPapel } from "@/features/pos/ticketEscpos";
import { cantidadCorta, fechaDelPapel } from "@/features/pos/ReciboEnCarta";
import { monedas as endpointMonedas, negocio } from "@/lib/endpoints";
import { hojasCartaComoPdf } from "@/lib/hojaCartaPdf";
import { llevaMarcaDeExento, nombresDeLosExentos } from "@/lib/exentos";
import { comoFormato, formatMoney, toNumber, type FormatoMoneda, monedaImpresa } from "@/lib/money";
import type { Cotizacion } from "@/lib/types";
import { soloFecha } from "./papelDeCotizacion";

const ID_DEL_PAPEL = "cotizacion-en-carta";

/** Imprime la cotización en hoja carta que está en pantalla. */
export function imprimirCotizacionEnCarta(): boolean {
  return imprimirHojaCarta(ID_DEL_PAPEL);
}

/** La cotización en hoja carta que está en pantalla, como PDF para mandar. */
export function cotizacionEnCartaComoPdf(): Blob | null {
  return hojasCartaComoPdf(ID_DEL_PAPEL);
}

/** «16» a partir de «0.1600». */
function porcentaje(tasa: string): string {
  const n = toNumber(tasa) * 100;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "").replace(".", ",");
}

/** La hoja, para la vista previa de la ventana cuando se elige «Hoja carta». */
export function CotizacionEnCarta({ cot }: { cot: Cotizacion }) {
  const { data: datosNegocio } = useQuery({ queryKey: ["negocio"], queryFn: negocio.obtener });
  const { data: listaMonedas } = useQuery({ queryKey: ["monedas"], queryFn: endpointMonedas.listar });
  const { varias } = useSucursales();

  const codigo = cot.currency_code;
  const formato: FormatoMoneda = (listaMonedas ?? []).map(comoFormato).find((m) => m.code === codigo) ?? {
    code: codigo,
    symbol: codigo,
    decimals: 2,
    symbol_before: false,
  };
  const importe = (valor: string | number) => formatMoney(valor, formato, { sinSimbolo: true });

  const cotizados = cot.impuestos.map((x) => x.tax_id);
  const exentosDe = nombresDeLosExentos(cot.items, cot.impuestos);

  const filas = cot.items.map((l) => ({
    id: l.id,
    celdas: [
      <strong key="c">{cantidadCorta(l.qty)}</strong>,
      <>
        {l.name}
        {l.variant_name && <span style={{ color: TENUE }}> · {l.variant_name}</span>}
        {llevaMarcaDeExento(l.exento_de, cotizados) && " (E)"}
        {toNumber(l.discount) > 0 && (
          <div style={{ color: TENUE, fontSize: "10.5px" }}>Descuento: −{importe(l.discount)}</div>
        )}
      </>,
      l.codigo ?? "—",
      importe(l.unit_price),
      <strong key="t">{importe(l.line_total)}</strong>,
    ],
  }));

  // ── La cuenta, como en el papel del rollo ──────────────────────────────────
  const sumados = cot.impuestos.filter((x) => x.suma_al_total);
  const avisados = cot.impuestos.filter((x) => !x.suma_al_total);
  const hayDescuento = toNumber(cot.discount_total) > 0;
  const sumaDePedido = sumados.reduce((acc, x) => acc + toNumber(x.monto), 0);
  const impuestoDeProductos = toNumber(cot.tax_total) - sumaDePedido;

  const totales: TotalDeHoja[] = [];
  if (hayDescuento || toNumber(cot.tax_total) > 0) {
    totales.push({ etiqueta: `Subtotal ${monedaImpresa(codigo)}`, valor: importe(cot.subtotal) });
  }
  if (hayDescuento) totales.push({ etiqueta: "Descuento", valor: `−${importe(cot.discount_total)}` });
  if (impuestoDeProductos > 0) totales.push({ etiqueta: "Impuesto", valor: importe(impuestoDeProductos) });
  for (const x of sumados) {
    if (exentosDe.includes(x.name)) totales.push({ etiqueta: `Base ${x.name}`, valor: importe(x.base_amount) });
    totales.push({ etiqueta: `${x.name} ${porcentaje(x.rate)}%`, valor: importe(x.monto) });
  }
  totales.push({ etiqueta: `Total ${monedaImpresa(codigo)}`, valor: importe(cot.total), fuerte: true });

  // Lo que se suma solo si paga en divisas (el IGTF): debajo, nunca dentro.
  const avisos = avisados.map((x) => {
    const monedas = (x.currency_codes ?? []).map(monedaImpresa).join("/");
    const conImpuesto = toNumber(cot.total) + toNumber(x.monto);
    return `${monedas ? `Si paga en ${monedas} se suma` : "Además se suma"} ${x.name} ${porcentaje(x.rate)}%: ${importe(x.monto)} ${monedaImpresa(codigo)} (total con este: ${importe(conImpuesto)} ${monedaImpresa(codigo)}).`;
  });

  const { direccion, telefono } = contactoDelPapel(cot, datosNegocio);
  const esVenezuela = datosNegocio?.country_code === "VE";

  return (
      <HojaCarta
        id={ID_DEL_PAPEL}
        ampliable={false}
        titulo="Cotización"
        numero={cot.numero}
        fecha={fechaDelPapel(cot.created_at)}
        emisor={{
          negocio: datosNegocio?.name ?? "",
          razonSocial: datosNegocio?.legal_name,
          logo: datosNegocio?.logo_url,
          rif: datosNegocio?.tax_id,
          sucursal: varias || cot.sucursal_es_principal === false ? cot.sucursal_nombre : null,
          direccion,
          telefono,
          domicilioFiscal: domicilioFiscalDe(datosNegocio, cot.sucursal_es_principal),
        }}
        datos={[
          { etiqueta: "Cliente", valor: cot.customer_name ?? cot.contacto ?? "—" },
          {
            etiqueta: esVenezuela ? "C.I./RIF" : "Documento",
            valor: cot.customer_documento ?? cot.customer_tax_id ?? "—",
          },
          ...(cot.customer_phone ? [{ etiqueta: "Teléfono", valor: cot.customer_phone }] : []),
          // ⚠️ En negrita: es lo que protege al negocio de la inflación.
          ...(cot.valida_hasta
            ? [{ etiqueta: "Válida hasta", valor: <strong>{soloFecha(cot.valida_hasta)}</strong> }]
            : []),
          ...(cot.creada_por_nombre ? [{ etiqueta: "Asesor", valor: cot.creada_por_nombre }] : []),
        ]}
        aviso={
          cot.vencida ? (
            <div
              style={{
                border: `2px solid ${ROJO}`,
                color: ROJO,
                padding: "6px 10px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                textAlign: "center",
              }}
            >
              Vencida: los precios deben confirmarse
            </div>
          ) : undefined
        }
        columnas={[
          { titulo: "Cantidad", ancho: 76 },
          { titulo: "Descripción" },
          { titulo: "SKU", ancho: 120 },
          { titulo: `Precio ${monedaImpresa(codigo)}`, ancho: 100, derecha: true },
          { titulo: `Total ${monedaImpresa(codigo)}`, ancho: 108, derecha: true },
        ]}
        filas={filas}
        resumen={`Total de productos: ${cot.items.length}`}
        totales={totales}
        aclaracion={
          <>
            {avisos.map((a) => (
              <div key={a} style={{ fontWeight: 700 }}>
                {a}
              </div>
            ))}
            <div>
              Esta es una cotización, no una factura.
              {cot.valida_hasta && ` Los precios son válidos hasta el ${soloFecha(cot.valida_hasta)}.`}{" "}
              Sujeta a disponibilidad.
            </div>
          </>
        }
        observaciones={cot.nota}
        despedida="Gracias por preferirnos"
      />
  );
}
